import {
  Request,
  CreateRequest,
  UpdateRequest,
  RequestFilters,
  OfflineServiceRequestPaymentResponse,
} from "@/types/request";
import { BaseService } from "./BaseService";
import {
  IGetRequestsResponse,
  IRequestService,
} from "./interfaces/IRequestService";
import { RegionUserProfile } from "./authService";

class RequestService extends BaseService implements IRequestService {
  private basePath = "/requests";

  constructor() {
    super();
  }

  async getRequests(filters?: RequestFilters): Promise<IGetRequestsResponse> {
    filters = filters || {};
    return this.get<IGetRequestsResponse>(this.basePath, filters);
  }

  async getRequestsCreators(): Promise<RegionUserProfile[]> {
    return this.get<RegionUserProfile[]>(`${this.basePath}/creators`);
  }

  async getRequestById(id: string): Promise<Request> {
    return this.get<Request>(`${this.basePath}/${id}`);
  }

  async createRequest(requestData: CreateRequest): Promise<Request> {
    return this.post<Request>(this.basePath, requestData);
  }

  async bulkDeleteRequests(ids: string[]): Promise<void> {
    return this.delete<void>(`${this.basePath}/bulk`, { ids });
  }

  async updateRequest(
    id: string,
    requestData: UpdateRequest,
  ): Promise<Request> {
    return this.patch<Request>(`${this.basePath}/${id}`, requestData);
  }

  async markServiceRequestPaidOffline(
    id: string,
  ): Promise<OfflineServiceRequestPaymentResponse> {
    return this.post<OfflineServiceRequestPaymentResponse>(
      `/payments/service-requests/${id}/offline`,
    );
  }

  async deleteRequest(id: string): Promise<void> {
    return this.delete<void>(`${this.basePath}/${id}`);
  }

  /**
   * Get requests with centralized filtering from context
   * This method can be used when you want to apply the global request filters
   * from the RequestContext without having to pass them manually
   */
  async getRequestsWithContextFilters(
    contextFilters: RequestFilters,
  ): Promise<IGetRequestsResponse> {
    return this.getRequests(contextFilters);
  }

  /**
   * Get filtered rooms count for pagination
   * This would typically be a separate API endpoint in a real implementation
   */
  async getRequestsCount(filters?: RequestFilters): Promise<number> {
    const requests = await this.get<Request[]>(
      `${this.basePath}/count`,
      filters,
    );
    return Array.isArray(requests) ? requests.length : 0;
  }
}

export const requestService = new RequestService();
